import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { UserX, UserCheck } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { getClientDetail, editClient, deactivateClient } from '../api/clientsApi';
import { useToast } from '../context/ToastContext';

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [address, setAddress] = useState('');
  const [addressDetails, setAddressDetails] = useState(null);

  const fetchClient = async () => {
    try {
      const result = await getClientDetail(id);
      if (result.success) {
        setClient(result.client);
        setAddress(result.client.address || '');
        reset({
          name: result.client.name,
          phone: result.client.phone,
          email: result.client.email,
          contact_name: result.client.contact_name,
          contact_phone: result.client.contact_phone,
          contact_email: result.client.contact_email,
        });
      } else {
        showToast('error', result.message || 'Failed to load client.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const result = await editClient({ id_client: id, ...data, address });
      if (result.success) {
        showToast('success', 'Client updated successfully.');
      } else {
        showToast('error', result.message || 'Failed to update client.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    const newStatus = client.is_active ? 0 : 1;
    try {
      const result = await deactivateClient(id, newStatus);
      if (result.success) {
        setClient((prev) => ({ ...prev, is_active: newStatus }));
        showToast('success', newStatus ? 'Client reactivated.' : 'Client deactivated.');
      } else {
        showToast('error', result.message || 'Failed to update client status.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    }
  };

  const loadColumns = [
    {
      accessorKey: 'id_load',
      header: 'Load #',
      cell: (info) => (
        <span
          style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 700 }}
          onClick={() => navigate(`/loads/${info.getValue()}`)}
        >
          #{info.getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'origin',
      header: 'Route',
      cell: (info) => (
        <span>
          {info.row.original.origin} <span style={{ color: '#9ca3af' }}>→</span> {info.row.original.destination}
        </span>
      ),
    },
    { accessorKey: 'driver_name', header: 'Driver', cell: (info) => info.getValue() || 'Unassigned' },
    { accessorKey: 'load_status', header: 'Status', cell: (info) => <StatusBadge status={info.getValue()} /> },
    {
      accessorKey: 'price_value',
      header: 'Price',
      cell: (info) => `${Number(info.getValue()).toLocaleString()} ${info.row.original.price_currency}`,
    },
  ];

  if (loading || !client) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <div style={{ flex: 1 }}>
          <TopBar title="Loading..." />
          <LoadingSpinner fullScreen />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={client.name}
          rightAction={
            <button
              onClick={handleToggleActive}
              style={{
                ...styles.toggleBtn,
                backgroundColor: client.is_active ? '#fee2e2' : '#d1fae5',
                color: client.is_active ? '#991b1b' : '#065f46',
              }}
            >
              {client.is_active ? (
                <>
                  <UserX size={15} style={{ marginRight: 6 }} /> Deactivate
                </>
              ) : (
                <>
                  <UserCheck size={15} style={{ marginRight: 6 }} /> Reactivate
                </>
              )}
            </button>
          }
        />

        <div style={{ padding: 24 }}>
          {!client.is_active && (
            <div style={styles.inactiveBanner}>
              This client is currently deactivated.
            </div>
          )}

          <div style={styles.grid}>
            <form onSubmit={handleSubmit(onSubmit)} style={styles.card}>
              <div style={styles.sectionTitle}>Company Information</div>

              <label style={styles.label}>Company Name</label>
              <input {...register('name', { required: true })} style={styles.input} />
              {errors.name && <p style={styles.errorText}>Required</p>}

              <div style={{ marginTop: 14 }}>
                <AddressAutocomplete
                  label="Address"
                  value={address}
                  onChange={setAddress}
                  onAddressSelect={setAddressDetails}
                />
              </div>

              <label style={styles.label}>Phone</label>
              <input {...register('phone')} style={styles.input} />

              <label style={styles.label}>Email</label>
              <input {...register('email')} type="email" style={styles.input} />

              <div style={styles.divider} />
              <div style={styles.sectionTitle}>Responsible Person</div>

              <label style={styles.label}>Name</label>
              <input {...register('contact_name')} style={styles.input} />

              <label style={styles.label}>Phone</label>
              <input {...register('contact_phone')} style={styles.input} />

              <label style={styles.label}>Email</label>
              <input {...register('contact_email')} type="email" style={styles.input} />

              <div style={styles.buttonRow}>
                <button type="submit" disabled={saving} style={styles.submitBtn}>
                  {saving ? '...' : 'Save Changes'}
                </button>
              </div>
            </form>

            <div>
              <div style={styles.sectionHeading}>Loads ({client.loads.length})</div>
              {client.loads.length === 0 ? (
                <div style={styles.emptyLoads}>No loads for this client yet.</div>
              ) : (
                <DataTable columns={loadColumns} data={client.loads} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '380px 1fr',
    gap: 24,
    alignItems: 'start',
  },
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  inactiveBanner: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 16,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#374151',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  sectionHeading: {
    fontSize: 15,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 12,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  errorText: { color: '#dc2626', fontSize: 12, marginTop: 4 },
  divider: { height: 1, backgroundColor: '#f3f4f6', margin: '20px 0 4px' },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', marginTop: 24 },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
  emptyLoads: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: 14,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
};

export default ClientDetail;